import axios from "axios";

function normalizeApiBase(raw) {
  let base = (raw || "").trim().replace(/\/+$/, "");

  if (typeof window !== "undefined") {
    const isHttps = window.location.protocol === "https:";
    if (!base) {
      base = `${window.location.origin}/api`;
    }
    if (isHttps && base.startsWith("http://")) {
      base = `https://${base.slice(7)}`;
    }
  }

  if (!/\/api$/.test(base)) {
    base = `${base}/api`;
  }

  return base;
}

export function getApiBase() {
  return normalizeApiBase(process.env.VUE_APP_API_BASE || "");
}

export function buildApiUrl(path = "") {
  const base = getApiBase();
  const normalizedPath = String(path).replace(/^\/+/, "");
  return normalizedPath ? `${base}/${normalizedPath}` : base;
}

export function getFrontendOrigin() {
  return typeof window !== "undefined" ? window.location.origin : "";
}

export function buildAddressCallbackUrl() {
  return buildApiUrl("address_callback");
}

export function buildAddressPopupUrl(confmKey) {
  const callback = encodeURIComponent(buildAddressCallbackUrl());
  return `https://business.juso.go.kr/addrlink/addrLinkUrl.do?confmKey=${confmKey}&returnUrl=${callback}`;
}

const api = axios.create({
  baseURL: getApiBase(),
});

/**
 * ✅ 401/만료 이벤트 중복 방지 (동시에 여러 요청이 401 나올 수 있음)
 */
let lastAuthExpiredAt = 0;
const COOLDOWN_MS = 1200;

function clearAuthStorage() {
  localStorage.removeItem("token");
  localStorage.removeItem("tokenExpiresAt");
  localStorage.removeItem("loggedInUser");
  localStorage.removeItem("id");
  localStorage.removeItem("userData");
}

function emitAuthExpired(message) {
  const now = Date.now();
  if (now - lastAuthExpiredAt < COOLDOWN_MS) return;
  lastAuthExpiredAt = now;

  clearAuthStorage();

  // ✅ 컴포넌트(sungho.vue)에서 받아서 alert + 강제 로그아웃 처리
  window.dispatchEvent(
    new CustomEvent("auth-expired", { detail: { message } })
  );
}

/**
 * ✅ 요청 인터셉터: 로컬 만료 체크 + Authorization 자동 부착
 */
api.interceptors.request.use(
  (config) => {
    const allowAnonymous = Boolean(config.allowAnonymous);
    if (allowAnonymous) return config;

    const token = localStorage.getItem("token");
    const tokenExpiresAt = Number(localStorage.getItem("tokenExpiresAt") || 0);

    // ✅ 서버에 요청하는 "그 순간"에 만료면 요청을 막고 로그아웃 이벤트
    if (!token || !tokenExpiresAt || Date.now() > tokenExpiresAt) {
      emitAuthExpired("로그인이 만료되었습니다. 다시 로그인해 주세요.");
      return Promise.reject(new Error("Token expired"));
    }

    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * ✅ 응답 인터셉터: 서버가 401 주면 즉시 로그아웃 이벤트
 */
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const allowAnonymous = Boolean(error?.config?.allowAnonymous);

    if (status === 401 && !allowAnonymous) {
      emitAuthExpired(
        "서버에서 로그아웃 처리되었습니다. 다시 로그인해 주세요."
      );
    }

    return Promise.reject(error);
  }
);

export default api;
